import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogContent,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { showToast } from '@utils';
import { apiInsertOrder } from '@store';

const STATUS_OPTIONS = ['Firm'];
const EXPIRY_OPTIONS = ['Good Till Cancelled'];
const CONTRACT_OPTIONS = ['DA', 'WD'];
const PRODUCT_OPTIONS = ['TTF', 'THE'];

const buildInsertPayload = (tradeEntry, values, action) => {
    const productData = tradeEntry?.row?.[tradeEntry?.productName] || {};
    const side = action === 'Bid' ? 'BID' : 'ASK';

    return {
        engine_id: String(productData.EngineId || '1'),
        price: Number(values.price),
        volume: Number(values.quantity),
        side,
        inst_name: values.product,
        first_sequence_id: Number(productData.FirstSequenceId || 0),
        first_sequence_item_name: values.contract,
        all_or_none: Boolean(values.allOrNone),
        broker_id: Number(productData.BrokerId || 1),
        route_id: Number(productData.RouteId || 1)
    };
};

const EMPTY_DEFAULTS = {
    price: '',
    quantity: '',
    allOrNone: false,
    status: 'Firm',
    expiry: 'Good Till Cancelled',
    contract: 'DA',
    product: 'TTF'
};

function InsertBidForm({ tradeEntry, onSubmit, loading }) {
    const defaults = useMemo(
        () => ({
            ...EMPTY_DEFAULTS,
            contract: tradeEntry?.row?.Contract || 'DA',
            product: tradeEntry?.productName || 'TTF'
        }),
        [tradeEntry]
    );
    const [values, setValues] = useState(defaults);

    useEffect(() => {
        setValues(defaults);
    }, [defaults]);

    const handleChange = (field) => (event) => {
        const nextValue =
            field === 'allOrNone' ? event.target.checked : event.target.value;
        setValues((cur) => ({ ...cur, [field]: nextValue }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        onSubmit(buildInsertPayload(tradeEntry, values, 'Bid'));
    };

    return (
        <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
                <Box
                    sx={{
                        px: 2,
                        py: 2,
                        borderRadius: 2,
                        bgcolor: '#eaf3fb',
                        textAlign: 'center'
                    }}
                >
                    <Typography
                        sx={{ fontSize: 28, fontWeight: 700, color: '#0f5b94' }}
                    >
                        {values.product}
                    </Typography>
                    <Typography
                        sx={{ fontSize: 20, fontWeight: 600, color: '#0f5b94' }}
                    >
                        {values.contract}
                    </Typography>
                </Box>

                <Stack direction="row" spacing={2}>
                    <TextField
                        label="Price"
                        type="number"
                        fullWidth
                        size="small"
                        value={values.price}
                        onChange={handleChange('price')}
                        inputProps={{ step: 'any' }}
                    />
                    <TextField
                        label="Quantity"
                        type="number"
                        fullWidth
                        size="small"
                        value={values.quantity}
                        onChange={handleChange('quantity')}
                        inputProps={{ step: 'any', min: 0 }}
                        disabled={values.allOrNone}
                        helperText={
                            values.allOrNone
                                ? 'Quantity is locked when AoN is enabled.'
                                : ''
                        }
                    />
                </Stack>

                <Stack direction="row" spacing={2} alignItems="center">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={values.allOrNone}
                                onChange={handleChange('allOrNone')}
                            />
                        }
                        label="AoN"
                    />
                </Stack>

                <Stack direction="row" spacing={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="ins-bid-contract-label">
                            Contract
                        </InputLabel>
                        <Select
                            labelId="ins-bid-contract-label"
                            label="Contract"
                            value={values.contract}
                            onChange={handleChange('contract')}
                        >
                            {CONTRACT_OPTIONS.map((c) => (
                                <MenuItem key={c} value={c}>
                                    {c}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                        <InputLabel id="ins-bid-product-label">
                            Product
                        </InputLabel>
                        <Select
                            labelId="ins-bid-product-label"
                            label="Product"
                            value={values.product}
                            onChange={handleChange('product')}
                        >
                            {PRODUCT_OPTIONS.map((p) => (
                                <MenuItem key={p} value={p}>
                                    {p}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>

                <Stack direction="row" spacing={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="ins-bid-status-label">
                            Status
                        </InputLabel>
                        <Select
                            labelId="ins-bid-status-label"
                            label="Status"
                            value={values.status}
                            onChange={handleChange('status')}
                        >
                            {STATUS_OPTIONS.map((s) => (
                                <MenuItem key={s} value={s}>
                                    {s}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                        <InputLabel id="ins-bid-expiry-label">
                            Expiry
                        </InputLabel>
                        <Select
                            labelId="ins-bid-expiry-label"
                            label="Expiry"
                            value={values.expiry}
                            onChange={handleChange('expiry')}
                        >
                            {EXPIRY_OPTIONS.map((e) => (
                                <MenuItem key={e} value={e}>
                                    {e}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>

                <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="space-between"
                >
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={
                            loading ||
                            values.price === '' ||
                            values.quantity === ''
                        }
                        sx={{
                            minWidth: 140,
                            bgcolor: '#29a000',
                            '&:hover': { bgcolor: '#228400' }
                        }}
                    >
                        {loading ? (
                            <CircularProgress
                                size={20}
                                sx={{ color: 'white' }}
                            />
                        ) : (
                            'Insert Bid'
                        )}
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
}

function InsertAskForm({ tradeEntry, onSubmit, loading }) {
    const defaults = useMemo(
        () => ({
            ...EMPTY_DEFAULTS,
            contract: tradeEntry?.row?.Contract || 'DA',
            product: tradeEntry?.productName || 'TTF'
        }),
        [tradeEntry]
    );
    const [values, setValues] = useState(defaults);

    useEffect(() => {
        setValues(defaults);
    }, [defaults]);

    const handleChange = (field) => (event) => {
        const nextValue =
            field === 'allOrNone' ? event.target.checked : event.target.value;
        setValues((cur) => ({ ...cur, [field]: nextValue }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        onSubmit(buildInsertPayload(tradeEntry, values, 'Ask'));
    };

    return (
        <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
                <Box
                    sx={{
                        px: 2,
                        py: 2,
                        borderRadius: 2,
                        bgcolor: '#fff3e0',
                        textAlign: 'center'
                    }}
                >
                    <Typography
                        sx={{ fontSize: 28, fontWeight: 700, color: '#b34800' }}
                    >
                        {values.product}
                    </Typography>
                    <Typography
                        sx={{ fontSize: 20, fontWeight: 600, color: '#b34800' }}
                    >
                        {values.contract}
                    </Typography>
                </Box>

                <Stack direction="row" spacing={2}>
                    <TextField
                        label="Price"
                        type="number"
                        fullWidth
                        size="small"
                        value={values.price}
                        onChange={handleChange('price')}
                        inputProps={{ step: 'any' }}
                    />
                    <TextField
                        label="Quantity"
                        type="number"
                        fullWidth
                        size="small"
                        value={values.quantity}
                        onChange={handleChange('quantity')}
                        inputProps={{ step: 'any', min: 0 }}
                        disabled={values.allOrNone}
                        helperText={
                            values.allOrNone
                                ? 'Quantity is locked when AoN is enabled.'
                                : ''
                        }
                    />
                </Stack>

                <Stack direction="row" spacing={2} alignItems="center">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={values.allOrNone}
                                onChange={handleChange('allOrNone')}
                            />
                        }
                        label="AoN"
                    />
                </Stack>

                <Stack direction="row" spacing={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="ins-ask-contract-label">
                            Contract
                        </InputLabel>
                        <Select
                            labelId="ins-ask-contract-label"
                            label="Contract"
                            value={values.contract}
                            onChange={handleChange('contract')}
                        >
                            {CONTRACT_OPTIONS.map((c) => (
                                <MenuItem key={c} value={c}>
                                    {c}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                        <InputLabel id="ins-ask-product-label">
                            Product
                        </InputLabel>
                        <Select
                            labelId="ins-ask-product-label"
                            label="Product"
                            value={values.product}
                            onChange={handleChange('product')}
                        >
                            {PRODUCT_OPTIONS.map((p) => (
                                <MenuItem key={p} value={p}>
                                    {p}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>

                <Stack direction="row" spacing={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="ins-ask-status-label">
                            Status
                        </InputLabel>
                        <Select
                            labelId="ins-ask-status-label"
                            label="Status"
                            value={values.status}
                            onChange={handleChange('status')}
                        >
                            {STATUS_OPTIONS.map((s) => (
                                <MenuItem key={s} value={s}>
                                    {s}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                        <InputLabel id="ins-ask-expiry-label">
                            Expiry
                        </InputLabel>
                        <Select
                            labelId="ins-ask-expiry-label"
                            label="Expiry"
                            value={values.expiry}
                            onChange={handleChange('expiry')}
                        >
                            {EXPIRY_OPTIONS.map((e) => (
                                <MenuItem key={e} value={e}>
                                    {e}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>

                <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="space-between"
                >
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={
                            loading ||
                            values.price === '' ||
                            values.quantity === ''
                        }
                        sx={{
                            minWidth: 140,
                            bgcolor: '#ff6d00',
                            '&:hover': { bgcolor: '#d45500' }
                        }}
                    >
                        {loading ? (
                            <CircularProgress
                                size={20}
                                sx={{ color: 'white' }}
                            />
                        ) : (
                            'Insert Ask'
                        )}
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
}

export default function EexInsertOrderModal({ open, tradeEntry, onClose }) {
    const dispatch = useDispatch();
    const isInsertOrderLoading = useSelector(
        (state) => state.eex.isInsertOrderLoading
    );

    const [activeTab, setActiveTab] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            setActiveTab(0);
        }
    }, [open]);

    const modalTitle = useMemo(() => {
        const productName = tradeEntry?.productName || 'EEX';
        const contractName = tradeEntry?.row?.Contract || 'Order';
        return `New Order — ${productName} ${contractName}`;
    }, [tradeEntry]);

    const handleSubmit = async (payload) => {
        setIsSubmitting(true);
        try {
            await dispatch(apiInsertOrder(payload)).unwrap();
            const actionLabel = payload.side === 'BID' ? 'Bid' : 'Ask';
            showToast('success', `${actionLabel} order inserted successfully`);
            onClose?.();
        } catch (error) {
            console.error(error);
            const errorMessage =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                'Failed to insert the EEX order';
            showToast('error', errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const loading = isInsertOrderLoading;

    return (
        <Dialog
            open={open}
            onClose={isSubmitting ? undefined : onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    px: 2,
                    pt: 2,
                    pb: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Typography
                    sx={{ fontWeight: 700, fontSize: 18, color: '#245372' }}
                >
                    {modalTitle}
                </Typography>
                <IconButton
                    onClick={onClose}
                    disabled={isSubmitting}
                    size="small"
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ px: 2, pt: 1 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, nextTab) => setActiveTab(nextTab)}
                    sx={{
                        minHeight: 42,
                        '& .MuiTabs-indicator': { backgroundColor: '#0b9cfd' }
                    }}
                >
                    <Tab
                        label="Insert Bid"
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            minHeight: 42
                        }}
                    />
                    <Tab
                        label="Insert Ask"
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            minHeight: 42
                        }}
                    />
                </Tabs>
            </Box>

            <DialogContent sx={{ px: 2, pb: 2, pt: 2 }}>
                {activeTab === 0 ? (
                    <InsertBidForm
                        tradeEntry={tradeEntry}
                        onSubmit={handleSubmit}
                        loading={loading}
                    />
                ) : (
                    <InsertAskForm
                        tradeEntry={tradeEntry}
                        onSubmit={handleSubmit}
                        loading={loading}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
}
